import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rendicionCampanaService, documentoService } from '../../../services/api';
import type { RendicionCampana } from './types';
import { LABEL_ETAPA, LABEL_TIPO_ELECCION } from './types';

interface Version {
  id: string;
  numeroVersion: number;
  hashCode: string;
  fechaGeneracion: string;
  pdfGeneradoUrl: string | null;
  usuario?: { nombre: string; apellido: string; email: string };
}

interface Snapshot {
  numeroVersion: number;
  hashCode: string;
  fechaGeneracion: string;
  snapshotData: {
    tipoPresentacion: string;
    tipoEleccion: string;
    etapa: string;
    fechaActoElectoral: string;
    codigoSpeca: string;
    documento?: { id: string; nombreOriginal: string; hashArchivo: string };
  };
}

interface Props {
  rendicion: RendicionCampana;
  partidoId: string;
  onBack: () => void;
  onPresentada: (info: { numeroExpediente: string; expedienteId: string; esRepresentacion?: boolean }) => void;
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Paso6FirmaEnvioDistrital({ rendicion, partidoId, onBack, onPresentada }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pdfDocId, setPdfDocId] = useState<string | null>(null);
  const [pdfNombre, setPdfNombre] = useState<string | null>(null);
  const [ddjjAceptada, setDdjjAceptada] = useState(false);
  const [uploading, setUploading] = useState(false);

  const rendicionId = rendicion.id;
  const readonly = rendicion.estado !== 'BORRADOR' && rendicion.estado !== 'OBSERVADA';
  const esRepresentacion = rendicion.estado === 'OBSERVADA';
  const tieneVersion = !!rendicion.hashActual && rendicion.versionActual > 0;

  const { data: versionesData } = useQuery({
    queryKey: ['rendicion-campana-versiones', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.listVersiones(rendicionId);
      return (res.data ?? []) as Version[];
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const versiones = versionesData ?? [];

  const { data: snapshotActual } = useQuery({
    queryKey: ['rendicion-campana-snapshot-actual', rendicionId, rendicion.versionActual],
    queryFn: async () => {
      const res = await rendicionCampanaService.getVersionSnapshot(rendicionId, rendicion.versionActual);
      return res.data as Snapshot;
    },
    enabled: tieneVersion,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const docVersionActual = snapshotActual?.snapshotData?.documento;
  const codigoSpecaVersion = snapshotActual?.snapshotData?.codigoSpeca;

  const hayPdfNuevoSinReflejar = !!pdfDocId && pdfDocId !== docVersionActual?.id;
  const hayCambioSpeca =
    tieneVersion && !!codigoSpecaVersion && rendicion.codigoSpeca !== codigoSpecaVersion;
  const hayCambiosDesdeUltimaVersion = hayPdfNuevoSinReflejar || hayCambioSpeca;

  const datosSpecaCargados = !!rendicion.codigoSpeca;

  function refrescarPaso() {
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana-versiones', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana-snapshot-actual', rendicionId] });
  }

  async function descargarPdf(numeroVersion: number) {
    try {
      const res = await rendicionCampanaService.descargarVersionPdf(rendicionId, numeroVersion);
      const url = res.data?.url;
      if (url) window.open(url, '_blank');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo descargar');
    }
  }

  async function subirPdf(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('tipo', 'COPIA_SPECA');
      formData.append('partidoId', partidoId);
      formData.append(
        'titulo',
        `Rendición Campaña ${LABEL_TIPO_ELECCION[rendicion.tipoEleccion]} ${rendicion.fechaActoElectoral.slice(0, 10)} — Copia SPECA ${rendicion.codigoSpeca ?? ''}`,
      );
      const res = await documentoService.upload(formData);
      const docId = res.data.data?.id ?? res.data.id;
      if (!docId) throw new Error('No se pudo subir el documento');
      setPdfDocId(docId);
      setPdfNombre(file.name);
      toast.success('PDF subido. Generá la versión para firmarlo con hash.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo subir');
    } finally {
      setUploading(false);
    }
  }

  const generarMutation = useMutation({
    mutationFn: async () => {
      const docId = pdfDocId ?? docVersionActual?.id;
      if (!docId) throw new Error('Subí primero el PDF de copia distrital');
      return rendicionCampanaService.generarVersionDistrito(rendicionId, { documentoId: docId });
    },
    onSuccess: (res) => {
      toast.success(`Versión N° ${res.data.numeroVersion} generada con hash`);
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-versiones', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-versiones-dropdown', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-snapshot-actual', rendicionId] });
      setPdfDocId(null);
      setPdfNombre(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo generar'),
  });

  const presentarMutation = useMutation({
    mutationFn: async () => {
      if (!ddjjAceptada) throw new Error('Debés aceptar la declaración jurada');
      return rendicionCampanaService.presentarCopiaDistrito(rendicionId, { declaracionJurada: true });
    },
    onSuccess: (res) => {
      toast.success(`Rendición presentada. Expediente ${res.data.numeroExpediente}`);
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendiciones-campana-partido', partidoId] });
      onPresentada({ ...res.data, esRepresentacion });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo enviar'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <button
          type="button"
          onClick={refrescarPaso}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Actualizar paso
        </button>
      </div>

      {/* Resumen Paso 1 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Resumen de datos (Paso 1)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500 uppercase">Tipo de elección</div>
            <div className="font-semibold">{LABEL_TIPO_ELECCION[rendicion.tipoEleccion]}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Etapa</div>
            <div className="font-semibold">{LABEL_ETAPA[rendicion.etapa]}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Fecha del acto</div>
            <div className="font-semibold">
              {new Date(rendicion.fechaActoElectoral).toLocaleDateString('es-AR')}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Código SPECA</div>
            <div className="font-mono text-xs break-all">{rendicion.codigoSpeca || '—'}</div>
          </div>
        </div>
        {!datosSpecaCargados && (
          <div className="mt-3 bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-900">
            Falta el código SPECA. Volvé al Paso 1 para completarlo antes de generar la versión.
          </div>
        )}
      </div>

      {/* Bloque 1 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          1. PDF de la copia SPECA y versión con hash
        </h2>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4 space-y-1">
          <p>
            <strong>Flujo:</strong>
          </p>
          <ol className="list-decimal ml-5 space-y-0.5 text-sm">
            <li>Subí el PDF de la presentación nacional (descargado de SPECA).</li>
            <li>Generá la versión: el sistema calcula un hash sobre el archivo + datos SPECA.</li>
            <li>En el bloque siguiente aceptá la DDJJ y enviá.</li>
          </ol>
          <p className="text-xs text-blue-800 mt-1">
            Si después subís otro PDF o cambiás los datos SPECA, tenés que regenerar la versión.
          </p>
        </div>

        {tieneVersion && hayCambiosDesdeUltimaVersion && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-900 mb-4 flex items-start gap-2">
            <span className="text-lg leading-none">⚠</span>
            <div>
              <strong>Hay cambios respecto a la versión actual.</strong>{' '}
              {hayPdfNuevoSinReflejar
                ? 'Subiste un PDF nuevo que todavía no se reflejó en una versión. '
                : ''}
              {hayCambioSpeca
                ? 'Editaste el código SPECA después de generar la versión. '
                : ''}
              Regenerá la versión antes de enviar.
            </div>
          </div>
        )}

        {tieneVersion && docVersionActual && !pdfDocId && (
          <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4 text-sm">
            <div className="text-xs text-gray-500 uppercase mb-1">PDF de la versión actual</div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-medium text-gray-800 break-all">{docVersionActual.nombreOriginal}</div>
              <button
                type="button"
                onClick={() => descargarPdf(rendicion.versionActual)}
                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
              >
                Descargar
              </button>
            </div>
          </div>
        )}

        {!readonly && (
          <div className="mb-4">
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              {tieneVersion ? 'Cambiar PDF (opcional)' : 'Subir PDF de copia SPECA'}{' '}
              {!tieneVersion && <span className="text-red-600">*</span>}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) subirPdf(f);
              }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 file:cursor-pointer disabled:opacity-50"
            />
            {uploading && <p className="mt-1 text-xs text-gray-600">Subiendo...</p>}
            {pdfDocId && pdfNombre && (
              <div className="mt-2 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-sm text-amber-900">
                ✓ PDF nuevo subido: <strong>{pdfNombre}</strong> — generá la versión para firmarlo con hash.
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            {tieneVersion ? (
              <div className="text-sm">
                <div className="text-gray-600">Versión actual generada</div>
                <div className="font-bold text-lg text-gray-900">N° {rendicion.versionActual}</div>
                <div className="text-xs font-mono text-gray-600 break-all">
                  Hash: {rendicion.hashActual}
                </div>
                {hayCambiosDesdeUltimaVersion && (
                  <div className="text-xs text-amber-700 font-semibold mt-1">
                    ⚠ Desactualizada: hay cambios sin reflejar
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">Todavía no se generó ninguna versión.</div>
            )}
          </div>
          {!readonly && (
            <button
              type="button"
              onClick={() => generarMutation.mutate()}
              disabled={
                generarMutation.isPending ||
                !datosSpecaCargados ||
                (!pdfDocId && !docVersionActual?.id)
              }
              title={
                !datosSpecaCargados
                  ? 'Faltan datos SPECA en el Paso 1'
                  : !pdfDocId && !docVersionActual?.id
                  ? 'Subí el PDF de copia SPECA'
                  : ''
              }
              className={`text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                tieneVersion && hayCambiosDesdeUltimaVersion
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {generarMutation.isPending
                ? 'Generando...'
                : tieneVersion
                ? '🔄 Regenerar versión'
                : '🔒 Generar versión con hash'}
            </button>
          )}
        </div>

        {versiones.length > 0 && (
          <div className="mt-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Historial de versiones</h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {versiones.map((v) => (
                    <tr key={v.id}>
                      <td className="px-3 py-2 font-semibold">
                        {v.numeroVersion === rendicion.versionActual ? (
                          <span className="text-green-700">N° {v.numeroVersion} ●</span>
                        ) : (
                          <span className="text-gray-500">N° {v.numeroVersion}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700 break-all max-w-[200px]">
                        {v.hashCode.slice(0, 16)}...
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{formatFecha(v.fechaGeneracion)}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">
                        {v.usuario ? `${v.usuario.nombre} ${v.usuario.apellido}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => descargarPdf(v.numeroVersion)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Descargar PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Bloque 2: DDJJ + envío */}
      {!readonly && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
            2. Declaración Jurada y Envío
          </h2>

          {esRepresentacion && (
            <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-r p-3 text-xs text-amber-900">
              Re-presentación tras observación — al enviar se generará una nueva versión y el expediente
              vinculado volverá al estado <strong>En análisis</strong>.
            </div>
          )}

          <label className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ddjjAceptada}
              onChange={(e) => setDdjjAceptada(e.target.checked)}
              className="mt-0.5"
              disabled={!tieneVersion}
            />
            <span className="text-sm text-gray-800">
              Declaro bajo juramento que el PDF adjuntado corresponde a la presentación realizada ante la
              Cámara Nacional Electoral a través de SPECA, y que sus contenidos reflejan fielmente los
              ingresos y egresos de la campaña electoral. <em>(Art. 53 inc. d, Ley XII-23)</em>
            </span>
          </label>

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3 border-t border-gray-200 pt-4">
            <div className="text-xs text-gray-600">
              Una vez enviada no podrá modificarse. La SEP tiene 30 días para aprobar o desaprobar{' '}
              <em>(Art. 54 Ley XII-23)</em>.
            </div>
            <button
              type="button"
              onClick={() => presentarMutation.mutate()}
              disabled={
                !tieneVersion ||
                hayCambiosDesdeUltimaVersion ||
                !ddjjAceptada ||
                presentarMutation.isPending
              }
              title={
                !tieneVersion
                  ? 'Falta generar la versión con hash'
                  : hayCambiosDesdeUltimaVersion
                  ? 'Hay cambios sin reflejar — regenerá la versión'
                  : !ddjjAceptada
                  ? 'Falta aceptar la declaración jurada'
                  : 'Enviar copia distrital'
              }
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-5 py-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {presentarMutation.isPending
                ? 'Enviando...'
                : esRepresentacion
                ? '✉️ Re-enviar Copia Distrital'
                : '✉️ Enviar Copia Distrital'}
            </button>
          </div>
        </div>
      )}

      {readonly && (
        <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 text-sm text-green-900">
          Esta rendición ya fue presentada (estado: <strong>{rendicion.estado}</strong>). No se puede
          modificar.
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
      </div>
    </div>
  );
}
